import { AnswerFormDocument, FormsAnswerFragment } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { Form } from 'modules/forms/lib/Form'

type saveAnswerProps = {
  /**
   * The form to save. It must have answers.
   */
  form: Form

  /**
   * The form membership Id to save answers to
   */
  membershipId: string

  /**
   * Success callback that will be called if answers are saved correctly
   * @param answers
   */
  onSuccess?: (answers: Array<FormsAnswerFragment> | undefined | null) => void

  /**
   * Error callback that will be called if an error ocurred
   * @param errors
   */
  onError?: (errors: Record<string, string>) => void
}

export const useAnswerForm = () => {
  const { company } = useCurrentSession()

  const [answer, { loading }] = useMutation(AnswerFormDocument, {
    extractorFn: (data) => data.formsMutations.bulkCreateFormsAnswer,
    update(cache, { data }) {
      const membershipId = data?.formsMutations.bulkCreateFormsAnswer.answers
        ? data?.formsMutations.bulkCreateFormsAnswer.answers[0]?.membership?.id
        : undefined
      if (membershipId) {
        cache.modify({
          id: `FormsMembership:${membershipId}`,
          fields: {
            answersConnection(existingAnswerConnection) {
              const existingTotalCount = existingAnswerConnection.totalCount || 0
              return {
                ...existingAnswerConnection,
                totalCount: existingTotalCount + 1,
              }
            },
          },
        })
      }
    },
  })

  return {
    savingAnswers: loading,
    saveAnswers: async ({ form, membershipId, onSuccess, onError }: saveAnswerProps) => {
      // TODO: Should validate all required answers exists and call onError instead of submitting?

      const result = await answer({
        variables: {
          answers:
            form?.getAnswers().map((ans) => ({
              answer: ans.answer,
              comment: ans.comment,
              elementId: ans.elementId,
              elementOptionIds: ans.options,
            })) ?? [],
          membershipId: membershipId,
          companyId: company.id.toString(), //TODO Remove here, don't send from frontend and get in the backend???
        },
      })

      const handled = handleResult(result, (errors) => {
        // call error callback if any or show a generic error
        if (onError) {
          onError(errors)
        } else {
          app.setFlash({
            type: 'wrong',
            message: i18n.t('common.error'),
          })
        }
      })

      //call success callback if any
      if (handled.success && onSuccess) {
        onSuccess(handled.data.answers)
      }
    },
  }
}
